import { useOktaAuth } from "@okta/okta-react";
import { useContext } from "react";
import {
  AppNotificationsApi,
  CreateChannelRequest,
  CreateChannelResponse,
  GetDealerAndUserScopedEventsRequest,
  GetEventsRequest,
  GetEventsResponse,
  PatchEventRequest,
  PatchEventResponse,
} from "./appNotificationInterfaces";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import { DelegateContext } from "../../context/DelegateContext/DelegateContext";
import axios from "../api";

const useAppNotificationApi = (): AppNotificationsApi => {
  const axiosV2FromContext = useContext(ApiContext);
  const { authState } = useOktaAuth();
  const { isDelegating, delegatedUserId } = useContext(DelegateContext);
  const {
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_APP_NOTIFICATION,
    APP_NOTIFICATION_SERVICE_URL: baseUrl,
  } = useContext(ConfigContext);
  const isAppNotificationApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_APP_NOTIFICATION;

  const apiUrl = `${baseUrl}/api/v1/event`;
  const apiV2Url = `${baseUrl}/api/v2/event`;
  const headers: Record<string, string | undefined> = {
    Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
    "X-id-token": authState?.idToken?.idToken,
    ...(isDelegating && delegatedUserId
      ? { "X-Delegate-User": delegatedUserId }
      : {}),
  };

  return {
    getAllEvents: async (
      queryParams: GetEventsRequest
    ): Promise<GetEventsResponse[]> => {
      const { dealerIds, ...rest } = queryParams;
      const response = isAppNotificationApiAuthEnabled
        ? await axiosV2FromContext.getWithRetries(apiUrl, {
            params: {
              ...rest,
              dealerIds: dealerIds.join(","),
            },
          })
        : await axios.getWithRetries(apiUrl, {
            headers,
            params: {
              ...rest,
              dealerIds: dealerIds.join(","),
            },
          });
      return response.data;
    },
    markEventsAsRead: async (
      request: PatchEventRequest[]
    ): Promise<PatchEventResponse> => {
      const response = isAppNotificationApiAuthEnabled
        ? await axiosV2FromContext.patchWithRetries(apiUrl, request)
        : await axios.patchWithRetries(apiUrl, request, { headers });
      return response.data;
    },
    createChannel: async (
      request: CreateChannelRequest
    ): Promise<CreateChannelResponse> => {
      const response = isAppNotificationApiAuthEnabled
        ? await axiosV2FromContext.postWithRetries(`${apiUrl}/channel`, request)
        : await axios.postWithRetries(`${apiUrl}/channel`, request, {
            headers,
          });
      return response.data;
    },
    keepChannelAlive: async (): Promise<CreateChannelResponse> => {
      const response = isAppNotificationApiAuthEnabled
        ? await axiosV2FromContext.patchWithRetries(
            `${apiUrl}/channel`,
            undefined
          )
        : await axios.patchWithRetries(`${apiUrl}/channel`, undefined, {
            headers,
          });
      return response.data;
    },
    getDealerAndUserScopedEvents: async (
      queryParams: GetDealerAndUserScopedEventsRequest
    ): Promise<GetEventsResponse[]> => {
      const { dealerIds, eventType, readStatus, userScopedCategories } =
        queryParams;
      const paramsSerializer = (params: any) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, item));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      };

      const response = isAppNotificationApiAuthEnabled
        ? await axiosV2FromContext.getWithRetries(apiV2Url, {
            params: {
              dealerIds,
              eventType,
              readStatus,
              userScopedCategories,
            },
            paramsSerializer,
          })
        : await axios.getWithRetries(apiV2Url, {
            headers,
            params: {
              dealerIds,
              eventType,
              readStatus,
              userScopedCategories,
            },
            paramsSerializer,
          });
      return response.data;
    },
  };
};

export default useAppNotificationApi;
